export const experimentAttributionFields = [
  'experiment_key',
  'experiment_variant',
  'experiment_assignment_source',
  'experiment_observation_id',
  'experiment_observed_at',
  'experiment_assignment_id',
  'experiment_assigned_at',
  'experiment_exposed_at',
  'experiment_campaign_id',
  'experiment_adgroup_id',
  'experiment_ad_id',
  'experiment_gclid',
  'experiment_gbraid',
  'experiment_wbraid',
] as const

export type ExperimentAttribution = Partial<
  Record<(typeof experimentAttributionFields)[number], string>
>

// Accept native URL observations and legacy assignments during deployment migration.
function completeTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.exec(
      value,
    )
  if (!match || !Number.isFinite(Date.parse(value))) return false
  const year = Number(match[1]),
    month = Number(match[2]),
    day = Number(match[3])
  const leap = year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0)
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1]
}

export function parseExperimentAttribution(
  raw: unknown,
): ExperimentAttribution | null {
  if (!raw || typeof raw !== 'object') return null
  const assignment: ExperimentAttribution = {}
  for (const key of experimentAttributionFields) {
    const value = (raw as Record<string, unknown>)[key]
    if (
      typeof value === 'string' &&
      value.trim() &&
      value.trim().length <= 2048
    )
      assignment[key] = value.trim()
  }
  const value = assignment
  const native = value.experiment_assignment_source === 'google_ads_url'
  const has = (key: string) => Object.prototype.hasOwnProperty.call(raw, key)
  const identity = native
    ? value.experiment_observation_id
    : value.experiment_assignment_id
  const timestamp = native
    ? value.experiment_observed_at
    : value.experiment_assigned_at
  if (
    (has('experiment_assignment_source') && !native) ||
    typeof value.experiment_key !== 'string' ||
    !value.experiment_key ||
    !['control', 'test'].includes(String(value.experiment_variant)) ||
    typeof identity !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      identity,
    ) ||
    !completeTimestamp(timestamp) ||
    !completeTimestamp(value.experiment_exposed_at) ||
    (native &&
      (has('experiment_assignment_id') || has('experiment_assigned_at'))) ||
    (!native &&
      (has('experiment_observation_id') || has('experiment_observed_at'))) ||
    !(['experiment_campaign_id', 'experiment_adgroup_id'] as const).every(
      (key) =>
        typeof value[key] === 'string' && /^\d+$/.test(value[key] as string),
    )
  )
    return null
  return assignment
}
